import { cn } from "@/shared/lib/utils";
import { AGENT_STATUS, TONE_CLASSES, type AgentStatus, type StatusMeta } from "./status";

type StatusBadgeProps = {
  /** Status de agente/ramal (atalho) … */
  status?: AgentStatus;
  /** … ou qualquer meta de status.ts (CALL_STATE, REG_STATE, CAMPAIGN_STATUS_META, CONTACT_STATUS_META…). */
  meta?: StatusMeta;
  /** Sobrescreve o rótulo (ex.: "Em pausa: Almoço"). */
  label?: string;
  size?: "sm" | "md";
  /** "badge" (padrão, fundo suave) | "dot" (só ponto + texto, para tabelas densas). */
  variant?: "badge" | "dot";
  /** Esconde o ícone (o ponto/forma continua). */
  hideIcon?: boolean;
  className?: string;
};

/**
 * Status com cor SEMÂNTICA + ícone + texto (nunca só cor — seguro para daltônicos).
 * As cores vêm dos tokens `--color-status-*` / tons semânticos (index.css).
 */
export function StatusBadge({ status, meta, label, size = "sm", variant = "badge", hideIcon, className }: StatusBadgeProps) {
  const m = meta ?? (status ? AGENT_STATUS[status] : AGENT_STATUS.OFFLINE);
  const t = TONE_CLASSES[m.tone];
  const Icon = m.icon;
  const text = label ?? m.label;

  if (variant === "dot") {
    return (
      <span className={cn("inline-flex min-w-0 items-center gap-1.5 text-body-sm font-medium text-fg", className)}>
        <span className={cn("relative h-2 w-2 shrink-0 rounded-full", t.dot, m.pulse && "animate-pulse-ring", t.text)} aria-hidden />
        <span className="truncate">{text}</span>
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-full font-medium",
        size === "sm" ? "h-5 px-2 text-caption" : "h-6 px-2.5 text-body-sm",
        t.subtle,
        className,
      )}
    >
      {hideIcon ? (
        <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", t.dot)} aria-hidden />
      ) : (
        <Icon className={cn("shrink-0", size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5", m.pulse && "animate-pulse")} strokeWidth={2.25} aria-hidden />
      )}
      <span className="truncate">{text}</span>
    </span>
  );
}
